import firebase from "@/javascript/firebase";

const db = firebase.firestore();
const date = firebase.firestore.FieldValue.serverTimestamp;

/**
 *  Writes to a document in a collection.
 *  If the document does not exist yet, it will be created.
 *  If you pass options, the provided data can be merged into the existing document.
 * @param {string} collection Name of the collection containing the document
 * @param {string} doc Name of the document to write to
 * @param {Object} values
 * @returns non-null Promise containing void. A promise that resolves once the data
 * has been successfully written to the backend (Note that it won't resolve while you're offline).
 */
const setDoc = async (collection, doc, values) => {
  const docRef = db.collection(collection).doc(doc);
  const created = date();
  const updated = date();
  values = { ...values, created, updated };
  // Don't overwrite the created field for documents that already exist
  const docSnap = await docRef.get();

  if (docSnap.exists) {
    const { created } = docSnap.data();
    values.created = created;
  }

  return docRef.set(values);
};

/**
 *  Insert/Update tags from genomic papers
 *  If the document does not exist yet, it will be created. Otherwise it shouldn't do anything.
 * @param {array} tags Array of tags
 * @returns non-null Promise containing void. A promise that resolves once the data
 * has been successfully written to the backend (Note that it won't resolve while you're offline).
 */
const upsertTags = async (tags) => {
  // Get a new write batch
  const batch = db.batch();

  tags.forEach((tag) => {
    const tagRef = db.collection("tags").doc(tag.replace(/\//g, ""));
    batch.set(tagRef, { tag });
  });

  return batch.commit();
};

/*
 * Get publication date using firestore timestamp
 * @param {string} publicationDate Publication date in format eg:- 09/2020
 * @returns firestore timestamp
 */
const getPublicationDateTS = (publicationDate) => {
  const { Timestamp } = firebase.firestore;

  const [month, year] = publicationDate.split("/").map(Number);
  return Timestamp.fromDate(new Date(`${month}/1/${year}`));
};

export { setDoc, upsertTags, getPublicationDateTS };
